import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Building2 } from "lucide-react";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { DataTable } from "@/components/data-table/DataTable";
import type { ColumnDef } from "@/components/data-table/types";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CountryDisplay } from "@/components/ui/country-combobox";
import { formatSectorLabel } from "@/lib/sector-labels";

type CompanyState = "active" | "disabled" | "invited";
type AssessmentStatus = "draft" | "complete" | "finalized";

interface PortfolioCompany {
  workspaceId: string | null;
  invitationId: string | null;
  companyName: string;
  country: string | null;
  sector: string | null;
  state: CompanyState;
  latestAssessment: {
    id: string;
    name: string;
    status: AssessmentStatus;
    locked: boolean;
    completenessPercent: number | null;
    updatedAt: string;
  } | null;
}

async function fetchCompanies(search: string, state: string): Promise<PortfolioCompany[]> {
  const params = new URLSearchParams();
  if (search) params.set("search", search);
  if (state !== "all") params.set("state", state);
  const res = await fetch(`/api/portfolio/companies?${params}`, { credentials: "include" });
  if (!res.ok) throw new Error("Failed to fetch companies");
  return res.json();
}

const STATE_BADGES: Record<CompanyState, { label: string; className: string; dot: string }> = {
  active: { label: "Active", className: "bg-emerald-50 text-emerald-600", dot: "bg-emerald-500" },
  disabled: { label: "Disabled", className: "bg-slate-100 text-slate-600", dot: "bg-slate-400" },
  invited: { label: "Invited", className: "bg-amber-50 text-amber-600", dot: "bg-amber-500" },
};

const STATUS_BADGES: Record<AssessmentStatus, { label: string; className: string; dot: string }> = {
  draft: { label: "Draft", className: "bg-slate-100 text-slate-600", dot: "bg-slate-400" },
  complete: { label: "Complete", className: "bg-blue-50 text-blue-600", dot: "bg-blue-500" },
  finalized: { label: "Finalised", className: "bg-emerald-50 text-emerald-600", dot: "bg-emerald-500" },
};

function StatusPill({ label, className, dot }: { label: string; className: string; dot: string }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${className}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
      {label}
    </span>
  );
}

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });

const isClickable = (company: PortfolioCompany) => company.state === "active" && !!company.workspaceId;

export default function PortfolioCompanies() {
  const [, setLocation] = useLocation();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search);
  const [stateFilter, setStateFilter] = useState<string>("all");

  const { data, isLoading, isError } = useQuery({
    queryKey: ["/api/portfolio/companies", debouncedSearch, stateFilter],
    queryFn: () => fetchCompanies(debouncedSearch, stateFilter),
  });

  const columns: ColumnDef<PortfolioCompany>[] = [
    {
      id: "company",
      header: "Company",
      cell: (c) => <span className="font-semibold text-slate-900">{c.companyName}</span>,
    },
    {
      id: "country",
      header: "Country",
      cell: (c) => <span className="text-slate-700"><CountryDisplay value={c.country} /></span>,
    },
    {
      id: "sector",
      header: "Sector",
      cell: (c) =>
        c.sector ? (
          <span className="px-2.5 py-1 bg-primary/10 rounded-full text-primary text-xs font-bold capitalize">
            {formatSectorLabel(c.sector)}
          </span>
        ) : (
          <span className="text-slate-400">-</span>
        ),
    },
    {
      id: "state",
      header: "State",
      cell: (c) => <StatusPill {...STATE_BADGES[c.state]} />,
    },
    {
      id: "latestAssessment",
      header: "Latest assessment",
      cell: (c) =>
        c.latestAssessment ? (
          <StatusPill {...STATUS_BADGES[c.latestAssessment.status]} />
        ) : (
          <span className="text-slate-400">-</span>
        ),
    },
    {
      id: "completeness",
      header: "Completeness",
      cell: (c) => {
        if (!c.latestAssessment) return <span className="text-slate-400">-</span>;
        const percent = c.latestAssessment.completenessPercent ?? 0;
        return (
          <div className="flex items-center gap-2">
            <div className="w-16 bg-slate-100 rounded-full h-2">
              <div className="bg-primary h-2 rounded-full" style={{ width: `${percent}%` }} />
            </div>
            <span className="text-sm font-medium text-slate-700">{percent}%</span>
          </div>
        );
      },
    },
    {
      id: "updated",
      header: "Updated",
      cell: (c) => (
        <span className="text-sm text-slate-500">
          {c.latestAssessment ? formatDate(c.latestAssessment.updatedAt) : "-"}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl font-extrabold tracking-tight text-slate-900">Companies</h1>
        <p className="text-slate-500 mt-2">Companies in your portfolio and pending invitations</p>
      </div>

      <div className="bg-card rounded-3xl shadow-sm outline outline-1 outline-offset-[-1px] outline-border overflow-hidden">
        <div className="px-6 py-5 border-b border-border/30">
          <div className="flex items-center gap-3">
            <div className="w-[43px] h-[43px] bg-primary/10 rounded-2xl flex items-center justify-center">
              <Building2 className="h-5 w-5 text-primary" />
            </div>
            <h3 className="text-xl font-bold text-foreground">{`Companies (${data?.length ?? 0})`}</h3>
          </div>
        </div>
        <div className="p-6">
          <DataTable
            columns={columns}
            data={data ?? []}
            getRowId={(c) => c.workspaceId ?? c.invitationId ?? c.companyName}
            isLoading={isLoading}
            emptyState={
              isError
                ? { icon: Building2, title: "Failed to load companies. Please refresh." }
                : { icon: Building2, title: "No companies found" }
            }
            search={{ value: search, onChange: setSearch, placeholder: "Search by company name..." }}
            filters={
              <Select value={stateFilter} onValueChange={setStateFilter}>
                <SelectTrigger className="w-[160px] rounded-2xl bg-slate-50">
                  <SelectValue placeholder="Filter by state" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All states</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="disabled">Disabled</SelectItem>
                  <SelectItem value="invited">Invited</SelectItem>
                </SelectContent>
              </Select>
            }
            onRowClick={(c) => {
              if (isClickable(c)) setLocation(`/portfolio/assessments?workspaceId=${c.workspaceId}`);
            }}
            // DataTable gives every row cursor-pointer once onRowClick is set; TableRow's cn()
            // lets this later class win for the invited/disabled rows, which don't link anywhere.
            rowClassName={(c) => (isClickable(c) ? "" : "cursor-default")}
          />
        </div>
      </div>
    </div>
  );
}
